import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, IconButton, Tooltip, LinearProgress, useTheme, useMediaQuery, Collapse } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDashboard } from '../../contexts/DashboardContext';
import { componentNeedsFlow, getComponent } from './controlRegistry';
import DashboardChat from './DashboardChat';
import DashboardSummary from './DashboardSummary';

export default function DashboardPanel({ panel, darkMode, bodyScroll = true }) {
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const isChat = panel?.type === 'chat';
  const isSummary = panel?.type === 'summary';
  const componentType = panel?.componentType;
  const outputKey = panel?.outputKey;
  const panelId = panel?.id ?? panel?._id;
  const hasFlow = Boolean(panel?.flowId || panel?.flow_id || panel?.source?.flowRef);
  const refreshOnMount = panel?.config?.refreshOnMount === true && hasFlow;
  const isCollapsible = panel?.config?.collapsible === true;
  const [collapsed, setCollapsed] = useState(panel?.config?.collapsedDefault === true);

  const { runPanelFlow, loadingPanelId, config: dashConfig } = useDashboard();
  const citizenMode = Boolean(
    dashConfig?.citizenMode || dashConfig?.metadata?.interviewFlow?.citizenMode,
  );

  useEffect(() => {
    if (refreshOnMount && panelId && runPanelFlow && !isChat) {
      runPanelFlow(panelId, {});
    }
  }, [refreshOnMount, panelId, runPanelFlow, isChat]);

  const handleRefresh = () => {
    if (panelId && runPanelFlow) runPanelFlow(panelId, {});
  };

  const renderComponent = () => {
    if (isChat) {
      return <DashboardChat darkMode={darkMode} />;
    }
    if (isSummary) {
      return <DashboardSummary />;
    }
    const Component = getComponent(componentType);
    if (!Component) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No component</Typography>
        </Box>
      );
    }
    return (
      <Component
        panelId={panelId}
        outputKey={outputKey}
        config={panel?.config}
      />
    );
  };

  const isPanelLoading = loadingPanelId === panelId;

  return (
    <Paper
      elevation={0}
      data-testid={`app-panel-${panelId}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: bodyScroll ? 1 : '0 0 auto',
        minHeight: bodyScroll ? 0 : undefined,
        height: bodyScroll ? '100%' : 'auto',
        overflow: bodyScroll ? 'hidden' : 'visible',
        borderRadius: 0,
        borderRight: panel?.position !== 'right' ? 1 : 0,
        borderColor: 'divider',
        boxShadow: '0 0 0 0 transparent',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
      }}
    >
      {!isChat && !isSummary && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {isCollapsible && (
              <IconButton size="small" onClick={() => setCollapsed((c) => !c)} sx={isSFF ? { minWidth: 44, minHeight: 44 } : {}}>
                {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </IconButton>
            )}
            {panel?.config?.title && (
              <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ px: 1 }}>
                {panel.config.title}
              </Typography>
            )}
            {!citizenMode && !hasFlow && componentNeedsFlow(componentType) && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                No flow configured — assign a flow in App Builder
              </Typography>
            )}
          </Box>
          {hasFlow && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={handleRefresh} disabled={isPanelLoading} sx={isSFF ? { minWidth: 44, minHeight: 44 } : {}}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {isPanelLoading && (
        <LinearProgress sx={{ width: '100%' }} />
      )}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: bodyScroll ? 'auto' : 'visible',
        overflowX: 'hidden',
        overscrollBehavior: bodyScroll ? 'contain' : undefined,
        WebkitOverflowScrolling: bodyScroll ? 'touch' : undefined,
        display: 'flex',
        flexDirection: 'column',
      }}
      >
        {isCollapsible ? (
          <Collapse in={!collapsed}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: collapsed ? 0 : 100 }}>
              {renderComponent()}
            </Box>
          </Collapse>
        ) : (
          renderComponent()
        )}
      </Box>
    </Paper>
  );
}
